import { DriveItemListOrigin } from '../containers/DriveItemListPage/constants';
import { Api } from '../typings/api';
import RoutesUtils from './routes';
import UserAgentUtils from './userAgent';
import Utils from './';
import { GalleryInfosState } from '../containers/Gallery/reducer';
import { Server, ASP_SERVERS } from '../containers/MultiAppAuthentication/constants';
import DriveItemUtils from './driveItem';
import ApiUtils from './api';
import store from '../store';
import { CustomSearchParams } from '../containers/DriveItemList/actions';

export enum ApiUrls {
	AUTH = 'services/rest/loginWs/',
	MEMBERSHIP = 'services/rest/membershipWs/',
	DRIVE = 'services/rest/workspaceManagerWs/',
	ENRICHMENT = 'services/rest/enrichmentWs/',
	GENERATION = 'services/rest/generationWs/',
	COMPONENT = 'services/rest/pageManagerWs/',
	GALLERY = 'services/rest/galleryManagerWs/',
	CUSTOMIZATION = 'services/rest/customizationWs/',
	LICENCE = 'services/rest/licenceWs/',
}

class UrlUtils {
	public static mediaParamRegex = /\$media\$|\${media}/;

	// ----- URL Generators
	// returns a the result of a final URL function
	public static getUrlAuthentication(galleryConfig: Api.Config, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathAuthentication(keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}


	public static getUrlHome(galleryConfig: Api.Config, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathHome(keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getUrlParameters(galleryConfig: Api.Config, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathParameters(keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getUrlDriveItemListPage(galleryConfig: Api.Config, origin: DriveItemListOrigin, originParam: string | null, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathDriveItemListPage(origin, originParam, keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getUrlDriveItemDetailPage(galleryConfig: Api.Config, driveItemId: number, driveFolderId: number|undefined, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathDriveItemDetailPage(driveItemId, driveFolderId, keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getUrlTheater(galleryConfig: Api.Config, origin: DriveItemListOrigin, originParam: string | null, driveItemId: number, isSharable: boolean) {
		const keepHashbang = isSharable;
		const hash = RoutesUtils.getPathTheater(origin, originParam, driveItemId, keepHashbang);
		return UrlUtils.getGallAppUrl(galleryConfig, hash);
	}

	public static getApiGalleryUrl(galleryConfig: Api.Config, api: string) {
		return this.getApiItemUrl(galleryConfig, galleryConfig.applicationGallery, api);
	}

	public static getChildrenUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_children.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getChildrenMetaUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_childrenMeta.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getAppMetaUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_appMeta.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getMetaUrl(galleryConfig: Api.Config, driveItemId: number, ancestorId?: number) {
		// childOf param is needed to get parent folder (linkParentId) id of linked resource (e.g., modifiable publication)
		const api = `_meta.json${ancestorId ? `?childOf=${ancestorId}` : ''}`;
		const url = UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);

		return url;
	}

	public static getContentBlockUrl(galleryConfig: Api.Config, driveItemId: number, block: number) {
		const api = `_content-block-${block}.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getResourceZipUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_resource.zip`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getOutcomingsUrl = (galleryConfig: Api.Config, driveItemId: number): string => {
		const api = `_outcomings.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getAttachmentUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_attachment`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getNewContentsUrl(galleryConfig: Api.Config) {
		const api = `_new.json`;

		return UrlUtils.getApiGalleryUrl(galleryConfig, api);
	}

	public static removeTrailingChar(queryString: string, character: string = '&') {
		return queryString.slice(-1) === character ? queryString = queryString.slice(0, -1) : queryString;
	}

	public static getSearchQuery(searchString: string) {
		let query = '';
		if (searchString) {
			const regex = /tags\[(.*)\]/g;
			const match = regex.exec(searchString);
			if (match) {
				// Remove the match from the searchString
				query += encodeURIComponent(UrlUtils.removeTrailingChar(searchString.replace(match[0], '').trim()));
				// Add each tag to the url
				const tagsArray = match[1].split(',');
				tagsArray.forEach(tag => {
					query += `&tag=${encodeURIComponent(tag.trim())}`;
				});
			} else {
				query += encodeURIComponent(searchString);
			}
		}
		return query;
	}

	public static getSearchUrl(galleryConfig: Api.Config, searchString: string) {
		const api = `_search.json?expr=${this.getSearchQuery(searchString)}`;
		return UrlUtils.getApiGalleryUrl(galleryConfig, api);
	}

	public static getDownloadPDFUrl(galleryConfig: Api.Config, searchString: string) {
		const api = `_dlSearchResults.pdf?expr=${this.getSearchQuery(searchString)}`;

		return UrlUtils.getApiGalleryUrl(galleryConfig, api);
	}

	public static getGalleryAssetUrl(galleryConfig: Api.Config, driveItemId: number) {
		let api = `_find.json?id=${driveItemId}`;
		return UrlUtils.getApiGalleryUrl(galleryConfig, api);
	}

	public static getWelcomePageUrl = (galleryInfos: GalleryInfosState): string => {
		const { template, config } = galleryInfos;
		if (template && template.welcomepage && template.welcomepage.url) {
			return UrlUtils.getItemUrl(config, Utils.getMediaId(template.welcomepage.url));
		}
		return '';
	}

	public static getTemplateUrl(fileName: string, galleryConfig: Api.Config, server?: Server ) {
		if (server) {
			const driveUrl = ASP_SERVERS[server].driveUrl;
			const templateUrl = `${driveUrl}${galleryConfig.clientId}/${galleryConfig.applicationGallery}/.gallery/config/${fileName}`;

			return Utils.getUrlForSecuredResource(templateUrl);
		}

		return UrlUtils.getConfigFileUrl(galleryConfig, fileName);
	}

	public static getConfigUrl(server: Server, clientId: number, galleryId: number): string {
		const driveUrl = ASP_SERVERS[server].driveUrl;
		const url = `${driveUrl}${clientId}/${galleryId}/.gallery/config/config.json`;

		return Utils.getUrlForSecuredResource(url);
	}

	public static getStatisticsUrl(galleryConfig: Api.Config) {
		const fileName = `statistics.xml`;

		return UrlUtils.getConfigFileUrl(galleryConfig, fileName);
	}

	public static getLoginJsonUrl(galleryConfig: Api.Config) {
		return `${this.getRootApiGallery(galleryConfig)}_login.json`;
	}

	public static getSamlAuthenticationPage(params: string) {
		return `https://fr.zone-secure.net/saml-login/${params}`;
	}

	public static isSamlIframeRedirectionUrl(iframeLocation: string, appConfig: Api.Config) {
		const galleryUrl = `${this.getRootApiGallery(appConfig)}`;
		return iframeLocation.includes(galleryUrl);
	}

	// ----- Final URLs
	// complete URL with credential if needed

	// Don't add credential to this url, url shared to contacts
	public static getGallAppUrl(galleryConfig: Api.Config, hash: string) {
		return `${this.getRootApiGallery(galleryConfig)}/#${hash}`;
	}

	public static getLoginToAspUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.AUTH}appLogin`);
	}

	public static getRefreshSessionUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.AUTH}refresh`);
	}

	public static getLogoutFromAspUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.AUTH}logout`);
	}

	public static getLogoutFromApplication(galleryConfig: Api.Config, isDifferentAppUrls?: boolean): string {
		// there can be different application urls from ASP_SERVERS and from galleryConfig
		if (isDifferentAppUrls) {
			const { galleryInfos: { config: { clientId, applicationGallery } } } = store.getState();
			const appUrl = ASP_SERVERS[ApiUtils.multiAppAuthenticationServer].driveUrl;
			return `${appUrl}${clientId}/${applicationGallery}_logout`;
		}

		return `${UrlUtils.getApplicationUrl(galleryConfig)}_logout`;
	}

	public static getResetAspPasswordUrl(galleryConfig: Api.Config, company: string): string {
		const driveUrl = ASP_SERVERS[ApiUtils.multiAppAuthenticationServer].driveUrl;
		const companyParam = company ? `?org=${encodeURIComponent(company)}` : '';

		return Utils.getUrlWithHttpsProtocol(`${driveUrl}login/app/${companyParam}#forgot_password`);
	}

	public static getCreateDemoAccountUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.MEMBERSHIP}createDemoAccount`);
	}

	public static getFreeUrlAppLoginUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.AUTH}extAuth`);
	}

	public static getCrmAccountsUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.MEMBERSHIP}getCrmContacts`);
	}

	public static getAuthMethodsUrl(galleryConfig: Api.Config): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.AUTH}getAuthMethods`);
	}

	public static getOrgLoginUrl(galleryConfig: Api.Config, orgName?: string): string {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.AUTH}orgLogin${orgName ? `?isApp=${UserAgentUtils.isMobileApp
}&org=${encodeURIComponent(orgName)}` : ''}`);
	}

	public static getLoginUrl(galleryConfig: Api.Config, login: string, password: string) {
		// TODO: Test this method when Secured Galleries applications login form will work again
		const url = UrlUtils.getLoginJsonUrl(galleryConfig);
		return UrlUtils.addCredentials(url, login, password);
	}

	public static getCheckSessionUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.AUTH}check`);
	}

	public static getItemOriginalFileUrl(galleryConfig: Api.Config, originalFile: string) {
		const url = `${UrlUtils.getRootApiClient(galleryConfig)}/${originalFile}`;

		return Utils.getUrlForSecuredResource(url);
	}

	static getUrlFromRelUrlWithMediaParam(relUrl: string, galleryConfig: Api.Config): string {
		return Utils.getUrlForSecuredResource(relUrl.replace(UrlUtils.mediaParamRegex, `${UrlUtils.getRootApiClient(galleryConfig)}/`));
	}

	public static getImageUrl(galleryConfig: Api.Config, driveItem: Api.DriveItem, size: Api.ResolutionSize, extension: string, isThumb: boolean) {
		const useIcon = driveItem.type !== Api.DriveItemType.Image || isThumb;
		const imageId = useIcon ? DriveItemUtils.getIconId(driveItem) : driveItem.id;
		const url = `${this.getRootApiItem(galleryConfig, imageId)}_${size.box ? 'box-' : ''}${size.width}x${size.height}.${extension}`;

		return Utils.getUrlForSecuredResource(url);
	}

	public static getPublicationUrl(
		galleryConfig: Api.Config, driveItem: Api.DriveItem, page: number = 1, isForMobileApp: boolean = false,
		isShare: boolean = false, hideWelcomePage: boolean = false, customSearchParams: CustomSearchParams = {}
	): string {
		let publicationUrlParams = this.getPublicationUrlParams(page, isForMobileApp, hideWelcomePage, customSearchParams);

		const url = `${this.getRootApiItem(galleryConfig, driveItem.id, !isShare)}/index.html${publicationUrlParams}`;

		if (isShare) {
			return url;
		}

		return Utils.getUrlForSecuredResource(url);
	}

	public static getPublicationUrlParams(page: number = 1, isForMobileApp: boolean = false, hideWelcomePage: boolean = false, customSearchParams: CustomSearchParams = {}) {
		let publicationUrlParams = '?';

		if (page !== 1) {
			const startPageParam = `startPage=${page}&`;
			publicationUrlParams += startPageParam;
		}
		if (customSearchParams) {
			let params = '';
			for (let [paramKey, paramValue] of Object.entries(customSearchParams)) {
				params += `${paramKey}=${paramValue}&`;
			}
			publicationUrlParams += params;
		}
		if (isForMobileApp) {
			// nativeReq param is needed to handle a case when calls are made to several domains (different application urls from ASP_SERVERS and from galleryConfig)
			const mobileAppParameters = 'isApp=true&backHome=true&nativeReq=true&';
			publicationUrlParams += mobileAppParameters;
		}
		if (hideWelcomePage) {
			const hideWelcomePageParam = 'initState=nowelcomepage';
			publicationUrlParams += hideWelcomePageParam;
		}

		// Remove "?" from URL if no params are added
		if (page === 1 && !isForMobileApp && !hideWelcomePage) {
			publicationUrlParams = '';
		}

		if (publicationUrlParams[publicationUrlParams.length - 1] === '&') {
			publicationUrlParams = publicationUrlParams.slice(0, -1);
		}

		return publicationUrlParams;
	}

	public static getPublicationCoverUrl(galleryConfig: Api.Config, driveItem: Api.DriveItem, pageIndex: number) {
		const rootUrl = UrlUtils.getRootApiClient(galleryConfig);
		const cover = driveItem.thumbsPath ? DriveItemUtils.getPublicationCover(driveItem.thumbsPath, pageIndex + 1) : '';
		const url = `${rootUrl}/${driveItem.id}/${cover}`;

		return Utils.getUrlForSecuredResource(url);
	}


	public static getResourcesGroupUrl = (galleryConfig: Api.Config, ids: number[]): string => {
		const { service, clientId } = galleryConfig;
		if (!service) {
			return '';
		}
		const parsedIds = encodeURIComponent(JSON.stringify(ids));
		const encodedToken = encodeURIComponent(service.token);
		return Utils.getUrlWithCorrectProtocol(`${service.host}/${service.download.service}?clientId=${clientId}&ids=${parsedIds}&token=${encodedToken}`);
	}

	public static getShareServiceUrl = (galleryConfig: Api.Config): string => {
		const { applicationGallery } = galleryConfig;
		const shareServiceUrl = `${UrlUtils.getConfiguredRootApiClient(galleryConfig)}/${applicationGallery}_share`;
		return Utils.getUrlForSecuredResource(shareServiceUrl);
	}

	public static getCreatePrivateFolderUrl = (galleryConfig: Api.Config): string => {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}createDriveWithLinks`);
	}

	public static getCreateExternalLinkUrl = (galleryConfig: Api.Config): string => {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}createExternalLink`);
	}

	public static getComponentUrl = (galleryInfos: GalleryInfosState, componentId: number): string => {
		const { config } = galleryInfos;
		const url = UrlUtils.getRootApiItem(config, componentId) + '/index.html';
		return Utils.getUrlForSecuredResource(url);
	}

	public static getRootApiClient(galleryConfig: Api.Config, forceDefaultDomain?: boolean) {
		const isMaster = Utils.isMaster(galleryConfig);

		if (isMaster) {
			return this.getMasterRootApi(galleryConfig);
		}

		const isMobileApp = UserAgentUtils.isMobileApp;

		if (isMobileApp || process.env.NODE_ENV !== 'production' || Utils.isEmbedMode()) {
			return `${UrlUtils.getConfiguredRootApiClient(galleryConfig, forceDefaultDomain)}`;
		}

		return `${this.getRelativeRootApi()}`;
	}

	public static getConfiguredRootApiClient(galleryConfig: Api.Config, forceDefaultDomain?: boolean) {
		const { clientId } = galleryConfig;
		return `${this.getConfiguredRootApi(galleryConfig, forceDefaultDomain)}/${clientId}`;
	}

	public static getRootApiGallery(galleryConfig: Api.Config) {

		const isMobileApp = UserAgentUtils.isMobileApp;
		if (isMobileApp) {
			const { applicationGallery } = galleryConfig;
			return this.getRootApiItem(galleryConfig, applicationGallery);
		}

		return `${window.location.origin}${window.location.pathname}`;
	}

	public static getIndesignEditorDataUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.ENRICHMENT}getPubliEditData`);
	}

	public static getHtmlEditorDataUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.COMPONENT}getPageEnrichmentInfo`);
	}

	public static getEditorResourceUrl(galleryConfig: Api.Config, resourceId?: number, relPath?: string) {
		const resourceRoot = UrlUtils.getRootApiItem(galleryConfig, resourceId);
		return Utils.getUrlWithHttpsProtocol(`${resourceRoot}${relPath ? '/' + relPath : ''}`);
	}

	public static getSaveIndesignEditorPageUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.ENRICHMENT}saveEditedEpubPage`);
	}

	public static getSaveEditedAccessiblePageUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.ENRICHMENT}saveEditedAccessiblePage`);
	}

	public static getSaveIndesignEditorConfigsUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.ENRICHMENT}savePubliConfigsJson`);
	}

	public static getSaveIndesignEditorThemesCssUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.ENRICHMENT}saveThemesCss`);
	}

	public static getSaveHtmlEditorPageUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.COMPONENT}saveComponentPage`);
	}

	public static getCreateComboUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}createComboPublication`);
	}

	public static getCreateComboWithExternalSpreadsUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}includeExtPages`);
	}

	public static getPublicationProgressUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}getPublicationProgress`);
	}

	public static getImportFileUrl(galleryConfig: Api.Config, fileName: string, isOptimizedImageImport?: boolean) {
		const host = UrlUtils.getHost(galleryConfig);
		const fileExtension = `.${Utils.getFileExtension(fileName)}`;
		const service = DriveItemUtils.imageWithPdfExtensions.includes(fileExtension) &&
			fileExtension !== '.svg' &&
			isOptimizedImageImport
				? 'importImage'
				: 'importFile';
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}${service}`);
	}

	public static getSetAsTemplateUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}createProposalTpl`);
	}

	public static getRemoveTemplateUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}deleteProposalTpl`);
	}

	public static getPublicationTemplatesUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = `_templates.json`;

		return UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);
	}

	public static getCreatePublicationFromMasterTemplateUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}copyPublication`);
	}

	public static getCreateProposalFromDesktopFileUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}createProposal`);
	}

	public static getCreateProposalFromDriveItemUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}convertToProposal`);
	}

	public static getSharePublicationUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}createSharedResourceLink`);
	}

	public static getRenameResourceUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}updateResourceName`);
	}

	public static getTrashResourcesUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}trashResources`);
	}

	public static getRemovePublicationPagesUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}deletePublicationPages`);
	}

	public static getMovePublicationPagesUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}movePages`);
	}

	public static getDuplicatePublicationPagesUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}duplicatePages`);
	}

	public static getReprocessPublicationThumbsUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}reprocessPagePrintables`);
	}

	public static getApplicationUrl(galleryConfig: Api.Config) {
		const { applicationGallery } = galleryConfig;
		return Utils.getUrlWithHttpsProtocol(this.getRootApiItem(galleryConfig, applicationGallery));
	}

	public static getProfileDataUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.MEMBERSHIP}getProfileInfo`);
	}

	public static getEditProfileDataUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.MEMBERSHIP}editProfile`);
	}

	public static getProposalsByTypeUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}getProposalsByType`);
	}

	public static getMoveResourcesOrderUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}moveResourcesOrder`);
	}

	public static getMoveResourcesUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}moveResources`);
	}

	public static getArchiveResourcesUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GALLERY}archiveResources`);
	}

	public static getNotesUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = '_notes.json';
		const url = UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);

		return url;
	}

	public static getSaveNoteUrl(galleryConfig: Api.Config, driveItemId: number) {
		const api = '_notes';
		const url = UrlUtils.getApiItemUrl(galleryConfig, driveItemId, api);

		return url;
	}

	public static getResourceUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}getResource`);
	}

	public static getCustomerContextUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}getCustomerContext`);
	}

	public static getCreateComponentUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.COMPONENT}createPage`);
	}

	public static getPublicationSettingsUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}getPublicationSettings`);
	}

	public static getSavePublicationSettingsUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}updatePublicationSettings`);
	}

	public static getPublicationPdfSettingsUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.CUSTOMIZATION}getPubliPdfInfo`);
	}

	public static getSavePublicationPdfOptionUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.CUSTOMIZATION}updatePdfOption`);
	}

	public static getImportPublicationPdfUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}updatePdfweb`);
	}

	public static getPublicationIntegrationUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}getPubliIntegrationInfo`);
	}

	public static getUpdateOriginalPublicationUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.GENERATION}copyTempVersion`);
	}

	public static getAcquireLockUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}acquireLock`);
	}

	public static getReleaseLockUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}releaseLock`);
	}

	public static getShareUsersUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.MEMBERSHIP}getShareUsers`);
	}

	public static getShareWithUserUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.MEMBERSHIP}shareWithUser`);
	}

	public static getChangeCoverImageUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.DRIVE}changeCoverImage`);
	}

	public static getPublishPriceUrl(galleryConfig: Api.Config) {
		const host = UrlUtils.getHost(galleryConfig);
		return Utils.getUrlWithHttpsProtocol(`${host}${ApiUrls.LICENCE}getPublishPrice`);
	}

	private static getItemUrl(galleryConfig: Api.Config, driveItemId: number) {
		const url = UrlUtils.getRootApiItem(galleryConfig, driveItemId);

		return Utils.getUrlForSecuredResource(url);
	}

	private static getApiItemUrl(galleryConfig: Api.Config, driveItemId: number, api: string) {
		const url = `${UrlUtils.getRootApiItem(galleryConfig, driveItemId)}${api}`;
		// TODO: check why needToken was needed
		return Utils.getUrlForSecuredResource(url);
	}

	private static getConfigFileUrl(galleryConfig: Api.Config, fileName: string) {
		const url = `${this.getRootApiGallery(galleryConfig)}/.gallery/config/${fileName}`;

		return Utils.getUrlForSecuredResource(url);
	}

	// ----- Root URLs
	// not used directly, Final Urls append things to it and add credentials if needed

	private static getRootApiItem(galleryConfig: Api.Config, driveItemId?: number, forceDefaultDomain?: boolean) {
		const rootApiClient = this.getRootApiClient(galleryConfig, forceDefaultDomain);
		let normalizedRootApiClient;

		if (driveItemId) {
			normalizedRootApiClient = rootApiClient.endsWith('/') ? rootApiClient.slice(0, -1) : rootApiClient;
		} else {
			normalizedRootApiClient = rootApiClient;
		}

		return `${normalizedRootApiClient}${driveItemId ? '/' + driveItemId : ''}`;
	}

	private static getMasterRootApi(galleryConfig: Api.Config): string {
		const { mediaPath } = galleryConfig;
		return `${mediaPath ? mediaPath : ''}`;
	}

	private static getConfiguredRootApi(galleryConfig: Api.Config, forceDefaultDomain: boolean = true): string {
		const { applicationUrl } = galleryConfig;
		const { driveUrl } = ASP_SERVERS[ApiUtils.multiAppAuthenticationServer];
		const { isMobileApp } = UserAgentUtils;
		const rootApi = isMobileApp && forceDefaultDomain ?
			driveUrl :
			applicationUrl;
		return Utils.getUrlWithCorrectProtocol(rootApi);
	}

	private static getRelativeRootApi(): string {
		// Get the part before /${customClientPath}/-/ or /${galleryId}
		return new URL('../', window.location.href).href;
	}

	// ----- Helpers
	private static addCredentials(url: string, login: string, password: string): string {
		const includesSeparator = url.includes('?');
		const startingSeparator = includesSeparator ? '&' : '?';
		const params: string[] = [];
		params.push(`user=${encodeURIComponent(login)}`);
		params.push(`pass=${encodeURIComponent(password)}`);

		return `${url}${startingSeparator}${params.join('&')}`;
	}

	private static getHost(galleryConfig: Api.Config): string {
		let host = '';

		if (UserAgentUtils.isMobileApp) {
			host = ASP_SERVERS[ApiUtils.multiAppAuthenticationServer].url;
		} else if (galleryConfig.service) {
			const { host: configHost, directHost: configDirectHost } = galleryConfig.service;
			host = Utils.isEmbedMode() || Utils.isOpenedFromAdmin()
				? configHost
				: configDirectHost || configHost;
		}

		return host[host.length - 1] === '/' ? host : host + '/';
	}
}

export const urlUtils = new UrlUtils;

export default UrlUtils;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/url.ts